window.Plaintext = window.Plaintext = (window.Plaintext || {});
window.Plaintext.FocusManagement = Plaintext.FocusManagement || {};

/*
 * This library handles tools for creating and managing focus.  You can do this by creating "focus links"
 * by calling createFocusLink() or createTrapLinkFromContainer().  When you want to enable the links, call enableFocusLinks(links).
 * And when the focus trap (or links) are no longer required, call disableFocusLinks(links).  You can also simply call
 * enableFocusTrap/disableFocusTrap on an element and have looping focus performed automatically.
 *
 * This model allows for situations where the visual tab order may not match the element tab order.  Usually a "createTrapLinkFromContainer"
 * is enough to provide a looping focus trap, but you can also create an appropriate focus sequence when, for instance, a hamburger menu
 * moves an element to a different visual location and the tab order needs to reflect that.
 *
 * Note that a "focus link" is actually a function that is called by the enable/disable functions that sets up the appropriate event
 * listeners.  It's not an object that can be changed once created.
 *
 * Exports:
 * onEsc/offEsc: Convenience functions to register and unregister escape-key handlers which are commonly used to escape the focus trap.
 * 
 * createFocusLink(from, to): Creates a tab sequence between the specified elements.  Tabbing from "from" takes the user to "to" and shift-tabbing 
 *    performs the opposite.  Usefulf or general focus control or as the third parameter to enableFocusTrap, to enable specific focus links within
 *    a trap context.
 * 
 * createTrapLinkFromContainer(el): Loops the final tabbable element in "el" to the first tabbable element, creating a trap.  You probably prefer
 *    to use enableFocusTrap than using this specifically.
 * 
 * enable/disableFocusLinks: Until enable is called, focus links don't do anything.  You probably won't use these directly if you're using this
 *    library just for focus traps.  These funtions are more fine-grained to allow other types of focus management on pages where the visual
 *    layout doesn't match the tab layout.
 *
 * enable/disableFocusTrap: Creates and enables a focus trap preventing focus-escape from a specific element
 *
 * refreshFocusTrap: Can be called when a focus trap's elements have been rearranged and need to be updated.
 * 
 */

(function() {
  const FocusManagement = window.Plaintext.FocusManagement;
  var listenerHasAdjustedFocusThisTick = false;
  var escListeners = [];
  var focusStack = [];
  
  /***** UTILS *****/
  
  /** Known issue: Cannot be used on position:fixed elements.
    *   The offsetParent check returns null if the element has any parent that is display: none.  However, Chrome also returns null 
    *   for elements with display: fixed.  This usually isn't a problem since our focus traps so far have been non-fixed elements.
    */
  function isAriaVisible(el) {
    if (!el) { return false; }
    if (el.getAttribute('aria-hidden')) { return false; }
    if (el.offsetParent === null) { return false; }
    
    return true;
  }
  
  /**
   * @param containers a single element or array of elements
   * @return array[Node] Elements that can be tabbed to within the containers (including the container)
   */
  function findTabbables(containers) {
    containers = Array.isArray(containers) ? containers : [ containers ];
    
    let allContainedNodes = [];
    containers.forEach(c => {
      const containedNodes = c.querySelectorAll('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], area[href], button, iframe, object, embed, [tabindex]:not([disabled]), [contenteditable]');
      allContainedNodes = allContainedNodes.concat(Array.from(containedNodes));
    });
    
    // Ensure unique values by passing the collection through a Set
    let nodes = Array.from(new Set(allContainedNodes));
    
    // Ensure nodes are visible in aria.  This _may_ not be desired behavious in the future or in some applications, but
    // it's good for now.
    nodes = nodes.filter(isAriaVisible);
    
    // Sort according to DOM ordering
    nodes.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_PRECEDING) ? 1 : -1); 

    return nodes;
  }
  
  
  /***** Tools *****/
  
  function onEsc(fn) {
    var handler = function(e) {
      if (e.key !== 'Esc' && e.key !== 'Escape') { return; }
      fn();
    };
    
    escListeners.push({ fn: fn, handler: handler });
    window.addEventListener("keyup", handler);
  }
  
  function offEsc(fn) {
    // side-effect alert!  This "filter" also unregisters the event listener.
    escListeners = escListeners.filter(function(listener) {
      if (listener.fn === fn) {
        window.removeEventListener("keyup", listener.handler);
        return false;
      }
      else {
        return true;
      }
    });
  }
  
  /**
   * @return a function for the "keydown" event that listens for tabs and automatically wraps tab from the fromEl to the toEl, and
   * vice versa for shift+tab.
   * 
   * Note that we'd normally use keyup handlers to listen for keys, but Tab specifically takes effect on a keydown
   * 
   * Parameters can be an element or a prioritized array.  For arrays, only the first visible element will be treated.
   * 
   * @param fromEl Element being tabbed from
   * @param toEl Target after tabbing from fromEl
   */
  function createFocusLink(fromEl, toEl) {
    return function(e) {
      // Prevent multiple tab overrides on a single event.
      if (listenerHasAdjustedFocusThisTick) { return; }

      if (e.key !== 'Tab') { return; }
      
      var nextElement;

      var elementFromElOrArray = function(elOrArray) {
        if (Array.isArray(elOrArray)) {
          var eligibleElements = elOrArray.filter(isAriaVisible);
          return (eligibleElements.length > 0) ? eligibleElements[0] : null;
        }
        else {
          return isAriaVisible(elOrArray) ? elOrArray : null;
        }
      };
      
      var visibleToEl = elementFromElOrArray(toEl);
      var visibleFromEl = elementFromElOrArray(fromEl);

      if (e.shiftKey) {
        if (document.activeElement === visibleToEl) {
          nextElement = visibleFromEl;
        }
      }
      else {
        if (document.activeElement === visibleFromEl) {
          nextElement = visibleToEl;
        }
      }
      
      if (nextElement) {
        nextElement.focus();
        e.preventDefault();
        listenerHasAdjustedFocusThisTick = true;
        window.setTimeout(function() { listenerHasAdjustedFocusThisTick = false; }, 0);
      }
    }
  }
  
  /**
   * @param links array of 2-element arrays.  The first index of each sub-array is fromEl, the second is toEl
   */
  function createFocusLinks(links) {
    return links.map(link => createFocusLink(link[0], link[1]));
  }
  
  /**
   * Create a focus link that loops the last focusable element in container to the first one.  Or an empty array.
   * 
   * @param containers element or array of elements
   */
  function createTrapLinkFromContainer(containers) {
    var elements = findTabbables(containers);
    if (!elements.length) { return null; }

    const trapLink = FocusManagement.createFocusLink(elements[elements.length - 1], elements[0]);
    return trapLink;
  }
  
  /**
   * Sets up event listeners for the specified focus link(s)
   *
   * @param links function|function[]|[{ from, to }] One or more functions created by createFocusLinkKeyDownListener
   */
  function enableFocusLinks(links) {
    links = Array.isArray(links) ? links : [links];
    links.forEach(function(link) { link && document.addEventListener('keydown', link); });
  }
  
  /**
   * Disables up event listeners for the specified focus link(s)
   *
   * @param links function|function[] One or more functions created by createFocusLinkKeyDownListener
   */
  function disableFocusLinks(links) {
    links= Array.isArray(links) ? links : [links];
    links.forEach(function(link) { link && document.removeEventListener('keydown', link); });
  }

  /**
   * @param containers element or array of elements
   * @param additionalFocusLinks function|function[]|[{from, to}] One or more functions created by createFocusLinkKeyDownListener, or a set of from/to pairs
   */  
  function enableFocusTrap(containers, newFocusedElement, additionalFocusLinks) {
    containers = Array.isArray(containers) ? containers : [ containers ];

    if (!containers || !containers.length || !containers[0]) {
      console.error('No containers specified');
      return;
    }
    
    if (containers[0].FocusManagement) {
      console.error("Focus trap enabled multiple times on...", containers);
      disableFocusTrap(containers);
    }

    const focusLinks = [];
    focusLinks.push(createTrapLinkFromContainer(containers));

    if (Array.isArray(additionalFocusLinks)) {
      additionalFocusLinks.forEach(link => {
        if (typeof(link) === 'function') {
          focusLinks.push(link);
        }
        else if (link.from && link.to) {
          focusLinks.push(FocusManagement.createFocusLink(link.from, link.to));
        }
        else {
          console.error("Could not create focus link from: ", link);
        }
      });
    }

    if (!newFocusedElement) {
      var tabbables = findTabbables(containers);
      if (tabbables.length) { newFocusedElement = tabbables[0]; }
    }
  
    containers[0].FocusManagement = { 
      focusLinks: focusLinks,
      focusBeforeTrap: document.activeElement,
      containers: containers
    };

    enableFocusLinks(focusLinks);

    /* This is often called in response to a click, which will set focus on its own.  
     * Set focus *after* the click resolves so the caller doesn't have a responsibility 
     * to cancel the default click event behaviour */
    newFocusedElement && window.setTimeout(function() {
      newFocusedElement.focus({ preventScroll: true }); 
    }, 1);
  }
  
  function disableFocusTrap(containers, focusAfterClose) {
    containers = Array.isArray(containers) ? containers : [ containers ];
    const options = containers[0].FocusManagement || {};
    focusAfterClose = focusAfterClose || options.focusBeforeTrap;

    FocusManagement.disableFocusLinks(options.focusLinks);
    delete containers[0].FocusManagement;
    focusAfterClose.focus();
  }
  
  function toggleFocusTrap(containers, enable) {
    enable ? enableFocusTrap(containers) : disableFocusTrap(containers);
  }
  
  function refreshFocusTrap(containers, newFocusedElement, additionalFocusLinks) {
    containers = Array.isArray(containers) ? containers : [ containers ];
    let focusBeforeTrap = null;
    
    if (containers[0].FocusManagement) {
      focusBeforeTrap = containers[0].FocusManagement.focusBeforeTrap;
      disableFocusTrap(containers);
    }
    
    enableFocusTrap(containers, newFocusedElement, additionalFocusLinks);
    
    if (focusBeforeTrap) {
      containers[0].FocusManagement.focusBeforeTrap = focusBeforeTrap;
    }
  }
  
  function pushFocus() {
    focusStack.push(document.activeElement);
  }
  
  function popFocus() {
    const el = focusStack.pop();
    el && el.focus();
    return el;
  }
  
  function toggleFocusables(node, enable) {
    enable ? enableFocusables(node) : disableFocusables(node);
  }
  
  function disableFocusables(node) {
    const focusables = findTabbables(node);

    focusables.forEach(f => {
      if (!f.hasAttribute('preserved-tab-index')) {
        const tabIndex = f.hasAttribute('tabindex') ? f.getAttribute('tabindex') : 'false';
        f.setAttribute('preserved-tab-index', tabIndex);
      }

      f.setAttribute('tabindex', -1);
    });
  }
  
  function enableFocusables(node) {
    const focusables = findTabbables(node);

    const fnEnable = f => {
      if (f.hasAttribute('preserved-tab-index')) {
        const preservedIndex = f.getAttribute('preserved-tab-index');
        f.removeAttribute('preserved-tab-index');
        
        if (preservedIndex === 'false') {
          f.removeAttribute('tabindex', preservedIndex);
        }
        else {
          f.setAttribute('tabindex', preservedIndex);
        }
      }
    }
    
    focusables.forEach(fnEnable);
    node.querySelectorAll('[preserved-tab-index]').forEach(fnEnable);
  }
  
  FocusManagement.onEsc = onEsc;
  FocusManagement.offEsc = offEsc;
  
  FocusManagement.createFocusLink = createFocusLink;
  FocusManagement.createFocusLinks = createFocusLinks;
  FocusManagement.createTrapLinkFromContainer = createTrapLinkFromContainer;

  FocusManagement.enableFocusLinks = enableFocusLinks
  FocusManagement.disableFocusLinks = disableFocusLinks;
  
  FocusManagement.enableFocusTrap = enableFocusTrap;
  FocusManagement.disableFocusTrap = disableFocusTrap;
  FocusManagement.toggleFocusTrap = toggleFocusTrap;
  FocusManagement.refreshFocusTrap = refreshFocusTrap;

  FocusManagement.pushFocus = pushFocus;
  FocusManagement.popFocus = popFocus;
  
  FocusManagement.enableFocusables = enableFocusables;
  FocusManagement.disableFocusables = disableFocusables;
  FocusManagement.toggleFocusables = toggleFocusables;
})();

export default window.Plaintext.FocusManagement;


/**
 * Copyright 2022 Plaintext Inc.
 *
 * Permission to use, copy, modify, and distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 */
